import * as R from "ramda";

const nf = window.NodeFilter;

export const shouldNotReach = node => nearestBy(x => x.hidden, node);

export const match = R.curry(
  (selector, node) => node.matches && node.matches(selector) // && !shouldNotReach(node)
);

export const matchesSelfOrDescendant = selector =>
  R.either(
    R.both(match(selector), R.identity),
    R.tryCatch(node => node.querySelector(selector), R.F)
  );

export function matcher(selector) {
  return function (node) {
    return match(selector, node) ? nf.FILTER_ACCEPT : nf.FILTER_SKIP;
  };
}

export function adjacentBy(root, elt, method, matcher) {
  var tw = document.createTreeWalker(root, nf.SHOW_ELEMENT, matcher, false);
  tw.currentNode = elt;
  tw[method]();
  /* cycle if we can't move any further */
  if (tw.currentNode === elt) {
    tw.currentNode = tw.root;
    if (method === "nextNode") tw.firstChild();
    else if (method === "previousNode") tw.lastChild();
  }
  return tw.currentNode;
}

export function adjacent(root, elt, method, match) {
  var tw = document.createTreeWalker(
    root,
    nf.SHOW_ELEMENT,
    { acceptNode: matcher(match) },
    false
  );
  tw.currentNode = elt;
  tw[method]();
  /* cycle if we can't move any further */
  if (tw.currentNode === elt) {
    tw.currentNode = tw.root;
    if (method === "nextNode") tw.firstChild();
    else if (method === "previousNode") tw.lastChild();
  }
  return tw.currentNode;
}

export function nextBy(root, elt, method, matcher) {
  var tw = document.createTreeWalker(root, nf.SHOW_ELEMENT, matcher, false);
  tw.currentNode = elt;
  tw[method]();
  /* Return the next node in method. Will be the same as original if can't move any further */
  return tw.currentNode;
}

export function next(root, elt, method, match) {
  return nextBy(root, elt, method, { acceptNode: matcher(match) });
}

const nearestBy = R.curry((fn, node) => {
  try {
    while (
      node && // node instanceof Element &&
      !fn(node)
    ) {
      node = node.parentNode;
    }
  } catch (e) {
    console.warn(e);
    node = null;
  }
  return node;
});

const nearestByTraversingRoot = R.curry((fn, node) => {
  try {
    while (
      node && // node instanceof Element &&
      !fn(node)
    ) {
      // console.log(
      //   R.compose(
      //     R.when(R.both(R.identity, R.has("host")), R.prop("host")),
      //     R.prop("parentNode")
      //   )(node)
      // );

      node = node.parentNode;
      if (node && node.host) node = node.host;
    }
  } catch (e) {
    console.warn(e);
    node = null;
  }
  return node;
});

const nearestOverRoot = R.curry((selector, node) =>
  nearestByTraversingRoot(match(selector), node)
);

const nearest = R.curry((selector, node) => nearestBy(match(selector), node));

const find = R.curry((selector, node) => node.querySelector(selector));

const activeElement = documentOrShadowroot => {
  let elt = documentOrShadowroot.activeElement;
  if (elt === documentOrShadowroot) return elt;
  if (elt.shadowRoot) return activeElement(elt.shadowRoot);
  return elt;
};

const lastWithin = (root, match) => {
  const tw = document.createTreeWalker(
    root,
    nf.SHOW_ELEMENT,
    { acceptNode: matcher(match) },
    false
  );
  return tw.lastChild();
};

const firstWithin = (root, match) => {
  const tw = document.createTreeWalker(
    root,
    nf.SHOW_ELEMENT,
    { acceptNode: matcher(match) },
    false
  );
  return tw.firstChild();
};

const textNodeFilter = {
  acceptNode: node =>
    node instanceof Text || node.tagName === "BR"
      ? window.NodeFilter.FILTER_ACCEPT
      : window.NodeFilter.FILTER_SKIP
};

const insertAtTextPos = (node, insertion, start) => {
  const tw = document.createTreeWalker(
    node,
    window.NodeFilter.SHOW_TEXT | window.NodeFilter.SHOW_ELEMENT,
    textNodeFilter
  );
  let charCount = 0;
  const hasText = tw.nextNode();
  if (hasText === null) {
    node.appendChild(insertion);
  } else {
    tw.previousNode();
    while (tw.currentNode) {
      if (tw.currentNode.tagName) {
        charCount += 1;
        const node = tw.currentNode;
        tw.nextNode();
        if (node === tw.currentNode) {
          if (tw.currentNode.parentNode === insertion) break;
          tw.currentNode.after(insertion);
          break;
        }

        continue;
      }
      if (charCount + tw.currentNode.length >= start) {
        if (tw.currentNode.parentNode === insertion) break;
        const offset = Math.max(0, start - charCount);
        tw.currentNode.splitText(offset);
        tw.currentNode.after(insertion);
        break;
      }
      charCount += tw.currentNode.length;
      tw.nextNode();
    }
  }
};
const thingsAtTextPos = (node, start) => {
  const tw = document.createTreeWalker(
    node,
    window.NodeFilter.SHOW_TEXT | window.NodeFilter.SHOW_ELEMENT,
    textNodeFilter
  );
  let charCount = 0;
  const hasText = tw.nextNode();
  if (hasText === null) {
    return null;
  } else {
    tw.previousNode();
    while (tw.currentNode) {
      if (tw.currentNode.tagName) {
        charCount += 1;
        const node = tw.currentNode;
        tw.nextNode();
        if (node === tw.currentNode) {
          return null;
        }
        continue;
      }
      if (charCount + tw.currentNode.length >= start) {
        return [tw.currentNode, start - charCount, charCount];
      }
      charCount += tw.currentNode.length;
      tw.nextNode();
    }
    return null;
  }
};

const nearestSelfOrAround = selector =>
  R.either(nearest(selector), node => node.querySelector(selector));

const isVisible = e =>
  Boolean(e.offsetWidth || e.offsetHeight || e.getClientRects().length);

const isFramed = () => window.parent !== window;

const waitfor = (ms = 200) =>
  new Promise((resolve, reject) => window.setTimeout(resolve, ms));

const relatesToAs = (position, target, node) => {
  position = position.toLowerCase();
  switch (position) {
    case "beforebegin":
      return target.previousSibling === node; // Check if Node immediately precedes Target
    case "afterbegin":
      return target.firstChild === node; // Check if Node is the first child
    case "beforeend":
      return target.lastChild === node; // Check if Node is the last child
    case "afterend":
      return target.nextSibling === node; // Check if Node immediately follows Target
    default:
      return false; // Unknown position
  }
};

export {
  relatesToAs,
  isVisible,
  nearest,
  nearestBy,
  nearestByTraversingRoot,
  nearestSelfOrAround,
  activeElement,
  lastWithin,
  firstWithin,
  insertAtTextPos,
  isFramed,
  thingsAtTextPos,
  find,
  waitfor,
  nearestOverRoot
};
